import type { StaffPermission } from '@/types/permissions'

/**
 * The left-nav placeholder groups for MS-1's empty shell, matching CRM-0's
 * mid-level specs: Customers (→ MS-2 Customer 360), Subscriptions (→ MS-2
 * billing ops), Support (→ MS-3), Status (→ MS-4), Flags (→ admin-portal-
 * specification §7, pulled forward into the shell nav per CRM-1's bd
 * description), Dashboards (→ MS-5). Every entry routes to a guarded
 * placeholder page — see `views/placeholders/`.
 */
export interface NavItem {
  key: string
  routeName: string
  labelKey: string
  permission: StaffPermission
  icon: string
  /** Which future CRM-0 milestone owns the real screen behind this item. */
  milestone: string
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    key: 'customers',
    routeName: 'customers',
    labelKey: 'nav.customers',
    permission: 'customer:read',
    icon: 'pi-users',
    milestone: 'MS-2 · Customer 360',
  },
  {
    key: 'billing',
    routeName: 'billing',
    labelKey: 'nav.billing',
    permission: 'billing:read',
    icon: 'pi-credit-card',
    milestone: 'MS-2 · Billing Ops',
  },
  {
    key: 'support',
    routeName: 'support',
    labelKey: 'nav.support',
    permission: 'support:read',
    icon: 'pi-comments',
    milestone: 'MS-3 · Support Ticketing',
  },
  {
    key: 'status',
    routeName: 'status',
    labelKey: 'nav.status',
    permission: 'status:read',
    icon: 'pi-shield',
    milestone: 'MS-4 · Public Status Platform',
  },
  {
    key: 'flags',
    routeName: 'flags',
    labelKey: 'nav.flags',
    permission: 'feature:read',
    icon: 'pi-flag',
    milestone: 'admin-portal-specification §7 · Feature Flags',
  },
  // CRM-12's real feature-status board editor — distinct from the `flags`
  // placeholder above despite sharing the `feature:*` permission prefix on
  // the backend (`PermFeatureWrite` was minted for §15 row 7 "Feature-flag
  // edit" and reused as-is for this module; see `types/features.ts`'s doc
  // comment). `GET /status/features` is ALSO gated on `feature:write`
  // server-side (no separate staff read permission exists for this board),
  // so this nav entry uses `feature:write` rather than `feature:read`.
  {
    key: 'features',
    routeName: 'features',
    labelKey: 'nav.features',
    permission: 'feature:write',
    icon: 'pi-compass',
    milestone: 'CRM-12 · Public Feature-Status Board',
  },
  {
    key: 'dashboards',
    routeName: 'dashboards',
    labelKey: 'nav.dashboards',
    permission: 'dashboards:read',
    icon: 'pi-chart-bar',
    milestone: 'MS-5 · Ops Dashboards',
  },
  // CRM-8's non-Flutter half (Kirana-oj1u): the help-centre article editor.
  // Not one of MS-1's original 6 placeholder groups — added once the real
  // backend (internal/modules/helparticles) existed, same as `customers`/
  // `billing`/`support` graduated from ComingSoonView to real screens above.
  {
    key: 'help',
    routeName: 'help',
    labelKey: 'nav.help',
    permission: 'help_article:read',
    icon: 'pi-book',
    milestone: 'CRM-8 · Help Centre',
  },
  // Kirana-6ipq: pre-provision a staff identity by email (with a role
  // attached) before that person's first OIDC login, plus grant/revoke role
  // controls for existing staff. `admin_owner`-only via `rbac:manage`.
  {
    key: 'staff',
    routeName: 'staff',
    labelKey: 'nav.staff',
    permission: 'rbac:manage',
    icon: 'pi-id-card',
    milestone: 'Kirana-6ipq · Staff invitations',
  },
  // Kirana-h5bd: staff visibility into the marketing landing page's
  // pre-launch waitlist signups (`GET /waitlist/signups`, gated server-side
  // on `dashboard:read` — `staffauth.PermDashboardRead`). Uses the SAME
  // `dashboards:read` (plural) nav-gating permission the `dashboards` entry
  // above already uses, matching this file's existing pluralized
  // convention for that permission string (cosmetically different from the
  // backend's singular `dashboard:read` constant, same as `status:read`
  // above being an existing, intentional mismatch — not something to "fix"
  // here).
  {
    key: 'waitlist',
    routeName: 'waitlist',
    labelKey: 'nav.waitlist',
    permission: 'dashboards:read',
    icon: 'pi-user-plus',
    milestone: 'Kirana-h5bd · Waitlist signups',
  },
] as const

export type NavKey = (typeof NAV_ITEMS)[number]['key']
